import React from 'react';
import PropTypes from 'prop-types';
import { CURRENT_TIMEFRAME, MAX_TIMEFRAME, TIMEFRAME_QP } from 'Components/ComponentLibrary/Organisms/ScenarioChart';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';

const ACTIONS = {
  INITIALIZE: 'initialize',
  SET_ALLOCATIONS_TIMEFRAME: 'set-allocations-timeframe',
  SET_CHART_TIMEFRAME: 'set-chart-timeframe',
};

const initialState = {
  allocationsTimeframe: CURRENT_TIMEFRAME,
  chartTimeframe: MAX_TIMEFRAME,
  isInitialized: false,
};

const LOOKS_LIKE_YEAR = /\d{4}/;

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.INITIALIZE:
      return { ...state, isInitialized: true };
    case ACTIONS.SET_ALLOCATIONS_TIMEFRAME: {
      if (
        action.timeframe === CURRENT_TIMEFRAME
        || !LOOKS_LIKE_YEAR.test(state.chartTimeframe)
      ) {
        return {
          ...state,
          allocationsTimeframe: action.timeframe,
        };
      }

      return {
        ...state,
        allocationsTimeframe: action.timeframe,
        chartTimeframe: action.timeframe,
      };
    }
    case ACTIONS.SET_CHART_TIMEFRAME: {
      if (!LOOKS_LIKE_YEAR.test(action.timeframe)) {
        return {
          ...state,
          chartTimeframe: action.timeframe,
        };
      }

      return {
        ...state,
        allocationsTimeframe: action.timeframe,
        chartTimeframe: action.timeframe,
      };
    }
    default:
      throw new Error(`Unknown FilterProvider action, ${action.type}`);
  }
};

const FilterContext = React.createContext({});

export const FilterProvider = ({ children }) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const {
    pageContentCollection,
    pageContentResource,
    setPageContentCollection,
  } = usePageContentContext();
  const [isLoading, setIsLoading] = React.useState(false);

  useComponentDidMount(() => {
    const url = new URL(pageContentCollection.link('self').attr('url'));
    const timeframe = url.searchParams.get(TIMEFRAME_QP);

    if (timeframe) {
      dispatch({ type: ACTIONS.SET_ALLOCATIONS_TIMEFRAME, timeframe });
    }

    dispatch({ type: ACTIONS.INITIALIZE });
  });

  React.useEffect(() => {
    const cb = async () => {
      if (!state.isInitialized) {
        return;
      }
      setIsLoading(true);

      const collection = await pageContentResource.link('self')
        .withQueryParams()
        .equal(TIMEFRAME_QP, state.allocationsTimeframe) // set new timeframe
        .get();
      setPageContentCollection(collection); // update page content
      setIsLoading(false);
    };
    cb();
  }, [state.allocationsTimeframe]);

  const memoizedValue = React.useMemo(
    () => ({
      ...state,
      setAllocationsTimeframe: timeframe => {
        dispatch({ type: ACTIONS.SET_ALLOCATIONS_TIMEFRAME, timeframe });
      },
      setChartTimeframe: timeframe => {
        dispatch({ type: ACTIONS.SET_CHART_TIMEFRAME, timeframe });
      },
      isTimeFrameLoading: isLoading,
    }),
    [dispatch, state, isLoading],
  );

  return (
    <FilterContext.Provider value={memoizedValue}>
      {children}
    </FilterContext.Provider>
  );
};

export const useFilterContext = () => {
  const context = React.useContext(FilterContext);

  if (context === undefined) {
    throw new Error('useFilterContext must be used within a FilterProvider');
  }

  return context;
};

FilterProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};
